/**
 * Reports Page - View secretary report history
 */

import { useState, useEffect } from 'react';
import { Sparkles, Calendar, Trash2, Mail, Package, DollarSign, AlertCircle, Eye } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import EmailListOverlay from '@/components/common/EmailListOverlay';
import { useSecretaryReports, useSecretaryReport, useDeleteSecretaryReport, useMarkReportAsRead } from '@/hooks/useSecretary';

export default function Reports() {
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [showEmailOverlay, setShowEmailOverlay] = useState(false);
  const { data: reports, isLoading, error } = useSecretaryReports({ limit: 100 });
  const { data: selectedReport } = useSecretaryReport(selectedReportId);
  const deleteReport = useDeleteSecretaryReport();
  const markAsRead = useMarkReportAsRead();

  const displayReport = selectedReport || (reports && reports.length > 0 ? reports[0] : null);

  // Mark report as read when displayed in the detail panel
  useEffect(() => {
    if (displayReport && !displayReport.read_at) {
      markAsRead.mutate(displayReport.id);
    }
  }, [displayReport?.id]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this report?')) {
      await deleteReport.mutateAsync(id);
      if (selectedReportId === id) {
        setSelectedReportId(null);
      }
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Boswell Briefs</h1>
        <p className="text-muted-foreground">
          View your past Boswell reports and insights
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Report History</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingSpinner />
            ) : error ? (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm text-red-600">Failed to load reports</p>
              </div>
            ) : reports && reports.length > 0 ? (
              <div className="space-y-2">
                {reports.map((report) => (
                  <button
                    key={report.id}
                    onClick={() => setSelectedReportId(report.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      (selectedReportId || reports[0].id) === report.id
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-900'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm font-medium truncate">
                          {new Date(report.generated_at).toLocaleDateString()}
                        </span>
                      </div>
                      <button
                        onClick={(e) => handleDelete(report.id, e)}
                        className="text-red-500 hover:text-red-700 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {new Date(report.generated_at).toLocaleTimeString()}
                    </p>
                    {report.stats && (
                      <div className="flex gap-2 mt-2">
                        <Badge variant="secondary" className="text-xs">
                          {report.stats.total || 0} emails
                        </Badge>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  No reports yet
                </p>
                <p className="text-xs text-muted-foreground">
                  Generate your first secretary report from the Dashboard
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Detail */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Report Details
            </CardTitle>
            {displayReport && (
              <p className="text-sm text-muted-foreground mt-1">
                Generated: {new Date(displayReport.generated_at).toLocaleString()}
              </p>
            )}
          </CardHeader>
          <CardContent>
            {displayReport ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="p-4 bg-white dark:bg-gray-900 rounded-lg border">
                  <div className="prose dark:prose-invert max-w-none">
                    <p className="text-base leading-relaxed whitespace-pre-wrap">
                      {displayReport.summary}
                    </p>
                  </div>
                </div>

                {/* Quick Stats */}
                {displayReport.stats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {/* View Emails Button */}
                    {displayReport.emails && displayReport.emails.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowEmailOverlay(true)}
                        className="flex items-center gap-2 justify-center h-auto py-3"
                      >
                        <Eye className="h-4 w-4" />
                        <div>
                          <div className="font-bold">{displayReport.stats.total || 0}</div>
                          <div className="text-xs text-muted-foreground">View Emails</div>
                        </div>
                      </Button>
                    )}

                    {displayReport.pending_replies && displayReport.pending_replies.length > 0 && (
                      <div className="p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800 text-center">
                        <Mail className="h-5 w-5 text-orange-500 mx-auto mb-1" />
                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                          {displayReport.pending_replies.length}
                        </div>
                        <div className="text-xs text-muted-foreground">Need Reply</div>
                      </div>
                    )}

                    {displayReport.upcoming_events && displayReport.upcoming_events.length > 0 && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 text-center">
                        <Calendar className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {displayReport.upcoming_events.length}
                        </div>
                        <div className="text-xs text-muted-foreground">Events</div>
                      </div>
                    )}

                    {displayReport.stats.spam > 0 && (
                      <div className="p-3 bg-white dark:bg-gray-900 rounded-lg border text-center">
                        <div className="text-2xl font-bold text-muted-foreground">
                          {displayReport.stats.spam}
                        </div>
                        <div className="text-xs text-muted-foreground">Spam</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Plugin Highlights */}
                {displayReport.plugin_insights?.highlights && displayReport.plugin_insights.highlights.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Interesting Items
                    </h4>
                    <div className="space-y-2">
                      {displayReport.plugin_insights.highlights.slice(0, 5).map((highlight, idx) => (
                        <div
                          key={idx}
                          className="p-3 bg-white dark:bg-gray-900 rounded-lg border flex items-start gap-3"
                        >
                          {highlight.type === 'price' ? (
                            <DollarSign className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Package className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{highlight.subject}</p>
                            <p className="text-xs text-muted-foreground">
                              From: {highlight.from}
                            </p>
                            <Badge variant="outline" className="mt-1 text-xs">
                              {highlight.data}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Insights */}
                {displayReport.ai_insights && displayReport.ai_insights.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      AI Insights
                    </h4>
                    <div className="space-y-2">
                      {displayReport.ai_insights.map((insight, idx) => (
                        <div
                          key={idx}
                          className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800"
                        >
                          <p className="text-sm font-medium mb-1">{insight.subject}</p>
                          <p className="text-xs text-muted-foreground">{insight.insight}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category Breakdown */}
                {displayReport.stats && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      View category breakdown
                    </summary>
                    <div className="mt-2 p-3 bg-white dark:bg-gray-900 rounded border space-y-1">
                      {Object.entries(displayReport.stats)
                        .filter(([key, val]) => key !== 'total' && val > 0)
                        .map(([category, count]) => (
                          <div key={category} className="flex justify-between text-xs">
                            <span className="capitalize">{category}</span>
                            <Badge variant="secondary" className="text-xs">
                              {count}
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </details>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  Select a report to view details
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email List Overlay */}
      {showEmailOverlay && displayReport?.emails && (
        <EmailListOverlay
          emailIds={displayReport.emails.map(e => e.id)}
          title={`Report Emails (${displayReport.emails.length})`}
          onClose={() => setShowEmailOverlay(false)}
        />
      )}
    </div>
  );
}
